import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload, Delete, Download, Save, Search } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useToastStateSync } from '../hooks/useToastStateSync';
import AppService from '../services/AppService';
import ReviewAnalysisService from '../services/ReviewAnalysisService';
import type { WorkItemExportPlatform } from '../services/ReviewAnalysisService';
import type { App } from '../types/app';
import type {
  ParsedReviewRow,
  ReviewActionItem,
  ReviewAnalysisReport,
  ReviewThemeInsight,
} from '../types/reviewAnalysis';

const MODELS = [
  { value: '', label: 'Default (Gemini 2.5 Flash Lite)' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gpt-4.1-mini', label: 'OpenAI GPT-4.1 Mini' },
];

const WORK_ITEM_EXPORT_PLATFORMS: Array<{ value: WorkItemExportPlatform; label: string }> = [
  { value: 'azure-devops', label: 'Azure DevOps (CSV)' },
  { value: 'jira', label: 'Jira (CSV)' },
  { value: 'clickup', label: 'ClickUp (CSV)' },
];
const REVIEW_SOURCE_OPTIONS = [
  { value: 'upload', label: 'Upload CSV / Paste Text' },
  { value: 'google-play', label: 'Pull from Google Play' },
] as const;

export default function ReviewAnalysis() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [savedReportsAppId, setSavedReportsAppId] = useState('');
  const [model, setModel] = useState('');
  const [parsedReviews, setParsedReviews] = useState<ParsedReviewRow[]>([]);
  const [manualReviewText, setManualReviewText] = useState('');
  const [reviewInputSource, setReviewInputSource] =
    useState<(typeof REVIEW_SOURCE_OPTIONS)[number]['value']>('upload');
  const [googlePlayPackageName, setGooglePlayPackageName] = useState('');
  const [googlePlayMaxResults, setGooglePlayMaxResults] = useState('50');
  const [googlePlayStartIndex, setGooglePlayStartIndex] = useState('');
  const [googlePlayToken, setGooglePlayToken] = useState('');
  const [googlePlayTranslationLanguage, setGooglePlayTranslationLanguage] =
    useState('');
  const [googlePlayNextPageToken, setGooglePlayNextPageToken] = useState('');
  const [fetchingGooglePlayReviews, setFetchingGooglePlayReviews] = useState(false);
  const [lastGooglePlayFetchMeta, setLastGooglePlayFetchMeta] = useState<{
    apiReviewCount: number;
    parsedReviewCount: number;
    totalResults?: number;
    resultPerPage?: number;
    startIndex?: number;
  } | null>(null);
  const [workItemExportPlatform, setWorkItemExportPlatform] =
    useState<WorkItemExportPlatform>('azure-devops');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentReport, setCurrentReport] = useState<ReviewAnalysisReport | null>(null);
  const [savedReports, setSavedReports] = useState<ReviewAnalysisReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });

  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId]
  );

  const parsedManualReviews = useMemo(
    () => ReviewAnalysisService.parseManualReviewText(manualReviewText),
    [manualReviewText]
  );

  const combinedReviews = useMemo(
    () => [...parsedReviews, ...parsedManualReviews],
    [parsedManualReviews, parsedReviews]
  );

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const userApps = await AppService.getUserApps(user.email);
    setApps(userApps);
  }, [user?.email]);

  const loadReports = useCallback(async () => {
    if (!user?.email || !savedReportsAppId) {
      setSavedReports([]);
      return;
    }
    const reports = await ReviewAnalysisService.getReportsByApp(user.email, savedReportsAppId);
    setSavedReports(reports);
  }, [savedReportsAppId, user?.email]);

  useEffect(() => {
    loadApps().catch((err) => {
      console.error(err);
      setError('Failed to load registered apps.');
    });
  }, [loadApps]);

  useEffect(() => {
    loadReports().catch((err) => {
      console.error(err);
      setError('Failed to load saved review analysis reports.');
    });
  }, [loadReports]);

  useEffect(() => {
    if (!selectedApp) {
      setGooglePlayPackageName('');
      return;
    }
    setGooglePlayPackageName(selectedApp.googlePlayPackageName || '');
  }, [selectedApp]);

  const handleUploadCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setError(null);
      const parsed = await ReviewAnalysisService.parseReviewsCSV(file);
      if (parsed.length === 0) {
        setError('No valid reviews found in the uploaded CSV.');
        return;
      }
      setParsedReviews(parsed);
      setSuccess(`Loaded ${parsed.length} reviews from CSV.`);
    } catch (parseError: any) {
      console.error(parseError);
      setError(parseError?.message || 'Failed to parse review CSV.');
    }
  };

  const handleRunAnalysis = async () => {
    if (!user?.email || !selectedApp) {
      setError('Select a registered app first.');
      return;
    }
    if (combinedReviews.length === 0) {
      setError('Upload, fetch, or paste reviews first.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await ReviewAnalysisService.analyzeReviews({
        userId: user.email,
        app: selectedApp,
        reviews: combinedReviews,
        model: model || undefined,
      });
      setCurrentReport(report);
      setTab(1);
      setSuccess('Review analysis completed.');
    } catch (analysisError: any) {
      console.error(analysisError);
      setError(analysisError?.message || 'Failed to analyze reviews.');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchGooglePlayReviews = async () => {
    if (!selectedApp) {
      setError('Select a registered app first.');
      return;
    }

    const packageName = String(googlePlayPackageName || '').trim();
    if (!packageName) {
      setError('Google Play package name is required to fetch reviews.');
      return;
    }

    const maxResults = Number(googlePlayMaxResults || 50);
    if (!Number.isFinite(maxResults) || maxResults < 1 || maxResults > 100) {
      setError('Max results must be between 1 and 100.');
      return;
    }

    const startIndexRaw = String(googlePlayStartIndex || '').trim();
    const startIndex =
      startIndexRaw.length > 0 ? Number(startIndexRaw) : undefined;
    if (
      startIndex !== undefined &&
      (!Number.isFinite(startIndex) || startIndex < 0)
    ) {
      setError('Start index must be 0 or higher.');
      return;
    }

    try {
      setFetchingGooglePlayReviews(true);
      setError(null);
      setSuccess(null);
      const result = await ReviewAnalysisService.fetchGooglePlayReviews({
        appId: selectedApp.appId,
        packageName,
        maxResults,
        startIndex,
        token: googlePlayToken.trim() || undefined,
        translationLanguage: googlePlayTranslationLanguage.trim() || undefined,
      });

      const manualText = result.reviews
        .map((review) => {
          const ratingPrefix =
            typeof review.rating === 'number' ? `${review.rating} - ` : '';
          return `${ratingPrefix}${review.body}`;
        })
        .join('\n\n');

      setParsedReviews([]);
      setManualReviewText(manualText);
      setGooglePlayNextPageToken(result.nextPageToken || '');
      setLastGooglePlayFetchMeta({
        apiReviewCount: result.apiReviewCount,
        parsedReviewCount: result.reviews.length,
        totalResults: result.pageInfo?.totalResults,
        resultPerPage: result.pageInfo?.resultPerPage,
        startIndex: result.pageInfo?.startIndex,
      });
      setSuccess(
        `${result.message} Added ${result.reviews.length} reviews to manual input.`
      );
    } catch (fetchError: any) {
      console.error(fetchError);
      setError(fetchError?.message || 'Failed to fetch Google Play reviews.');
    } finally {
      setFetchingGooglePlayReviews(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;
    setSaving(true);
    try {
      await ReviewAnalysisService.saveReport(currentReport);
      await loadReports();
      setSuccess('Review analysis report saved.');
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || 'Failed to save report.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (report: ReviewAnalysisReport) => {
    if (!user?.email) return;
    if (!window.confirm('Delete this review analysis report?')) return;
    try {
      await ReviewAnalysisService.deleteReport(user.email, report.reportId);
      await loadReports();
      if (currentReport?.reportId === report.reportId) {
        setCurrentReport(null);
      }
      setSuccess('Report deleted.');
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || 'Failed to delete report.');
    }
  };

  const handleExportAllWorkItems = () => {
    if (!currentReport) return;
    try {
      ReviewAnalysisService.exportAllWorkItems({
        report: currentReport,
        platform: workItemExportPlatform,
      });
      setSuccess('Exported all work items.');
    } catch (exportError: any) {
      console.error(exportError);
      setError(exportError?.message || 'Failed to export work items.');
    }
  };

  const handleExportSingleWorkItem = (
    item: ReviewActionItem,
    category: 'bad-review-actions' | 'good-review-enhancements'
  ) => {
    if (!currentReport) return;
    try {
      ReviewAnalysisService.exportWorkItem({
        report: currentReport,
        item,
        category,
        platform: workItemExportPlatform,
      });
      setSuccess('Exported work item.');
    } catch (exportError: any) {
      console.error(exportError);
      setError(exportError?.message || 'Failed to export work item.');
    }
  };

  const avgRating =
    combinedReviews.filter((item) => typeof item.rating === 'number').length > 0
      ? combinedReviews
          .filter((item) => typeof item.rating === 'number')
          .reduce((sum, item) => sum + Number(item.rating || 0), 0) /
        combinedReviews.filter((item) => typeof item.rating === 'number').length
      : undefined;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Review Analysis
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload review CSV, paste review text, or pull Google Play reviews directly, then analyze what your app does well and what to improve.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="New Analysis" />
          <Tab label="Current Report" />
          <Tab label="Saved Reports" />
        </Tabs>
      </Box>

      {tab === 0 ? (
        <Paper sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Select a registered app"
                value={selectedAppId}
                onChange={(event) => setSelectedAppId(event.target.value)}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Review Input Source"
                value={reviewInputSource}
                onChange={(event) =>
                  setReviewInputSource(
                    event.target
                      .value as (typeof REVIEW_SOURCE_OPTIONS)[number]['value']
                  )
                }
              >
                {REVIEW_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Model (Optional)"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {MODELS.map((option) => (
                  <MenuItem key={option.value || 'default'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {reviewInputSource === 'upload' && (
              <Grid item xs={12}>
                <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                  Upload Reviews CSV
                  <input hidden accept=".csv,text/csv" type="file" onChange={handleUploadCSV} />
                </Button>
              </Grid>
            )}
            {reviewInputSource === 'google-play' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Google Play Package Name"
                    placeholder="com.example.app"
                    value={googlePlayPackageName}
                    onChange={(event) => setGooglePlayPackageName(event.target.value)}
                    helperText="Uses selected app package by default."
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Results"
                    value={googlePlayMaxResults}
                    onChange={(event) => setGooglePlayMaxResults(event.target.value)}
                    inputProps={{ min: 1, max: 100 }}
                    helperText="1 to 100"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Start Index (Optional)"
                    value={googlePlayStartIndex}
                    onChange={(event) => setGooglePlayStartIndex(event.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Page Token (Optional)"
                    placeholder="Next page token"
                    value={googlePlayToken}
                    onChange={(event) => setGooglePlayToken(event.target.value)}
                    helperText="Use for pagination from prior fetch."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Translation Language (Optional)"
                    placeholder="en-US"
                    value={googlePlayTranslationLanguage}
                    onChange={(event) =>
                      setGooglePlayTranslationLanguage(event.target.value)
                    }
                    helperText="Language code for translated review text."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleFetchGooglePlayReviews}
                      disabled={fetchingGooglePlayReviews || !selectedApp}
                    >
                      {fetchingGooglePlayReviews
                        ? 'Fetching Reviews...'
                        : 'Fetch Google Play Reviews'}
                    </Button>
                    {googlePlayNextPageToken ? (
                      <Chip
                        color="info"
                        label={`Next token: ${googlePlayNextPageToken.slice(0, 32)}...`}
                      />
                    ) : null}
                  </Box>
                </Grid>
                {lastGooglePlayFetchMeta ? (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      API reviews returned: {lastGooglePlayFetchMeta.apiReviewCount} | Parsed for analysis:{' '}
                      {lastGooglePlayFetchMeta.parsedReviewCount}
                      {typeof lastGooglePlayFetchMeta.totalResults === 'number'
                        ? ` | Total available: ${lastGooglePlayFetchMeta.totalResults}`
                        : ''}
                      {typeof lastGooglePlayFetchMeta.resultPerPage === 'number'
                        ? ` | Result/page: ${lastGooglePlayFetchMeta.resultPerPage}`
                        : ''}
                      {typeof lastGooglePlayFetchMeta.startIndex === 'number'
                        ? ` | Start index: ${lastGooglePlayFetchMeta.startIndex}`
                        : ''}
                    </Alert>
                  </Grid>
                ) : null}
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Review Text Input"
                placeholder={'Paste review content here.\nUse one review per line, or separate reviews with blank lines.\nOptional rating format per line: "5 - Great app but needs dark mode".'}
                value={manualReviewText}
                onChange={(event) => setManualReviewText(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Loaded reviews: {combinedReviews.length} (CSV: {parsedReviews.length}, Manual: {parsedManualReviews.length}){' '}
                {avgRating !== undefined ? `| Average rating: ${avgRating.toFixed(2)}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleRunAnalysis}
                disabled={loading || !selectedApp || combinedReviews.length === 0}
              >
                {loading ? 'Analyzing...' : 'Run Review Analysis'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {tab === 1 ? (
        !currentReport ? (
          <Alert severity="info">Run analysis first or open a saved report.</Alert>
        ) : (
          <Box>
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {currentReport.appName} Review Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reviews: {currentReport.reviewCount}
                    {typeof currentReport.avgRating === 'number'
                      ? ` | Avg rating: ${currentReport.avgRating.toFixed(2)}`
                      : ''}
                    {` | Model: ${currentReport.modelUsed}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    select
                    size="small"
                    label="Work Item Export Format"
                    value={workItemExportPlatform}
                    onChange={(event) =>
                      setWorkItemExportPlatform(event.target.value as WorkItemExportPlatform)
                    }
                    sx={{ minWidth: 220 }}
                  >
                    {WORK_ITEM_EXPORT_PLATFORMS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" startIcon={<Download />} onClick={handleExportAllWorkItems}>
                    Export All Work Items
                  </Button>
                  <Button variant="contained" startIcon={<Save />} onClick={handleSaveReport} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Report'}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {currentReport.analysis.summary}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InsightSection title="What The App Is Doing Well" insights={currentReport.analysis.whatIsWorkingWell} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InsightSection title="What Needs Improvement" insights={currentReport.analysis.whatNeedsImprovement} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ActionSection
                  title="Action Items For Bad Reviews"
                  items={currentReport.analysis.badReviewActionItems}
                  onExportItem={(item) => handleExportSingleWorkItem(item, 'bad-review-actions')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ActionSection
                  title="Enhancement Actions From Good Reviews"
                  items={currentReport.analysis.goodReviewEnhancementItems}
                  onExportItem={(item) =>
                    handleExportSingleWorkItem(item, 'good-review-enhancements')
                  }
                />
              </Grid>
            </Grid>
          </Box>
        )
      ) : null}

      {tab === 2 ? (
        <Box>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <TextField
              select
              fullWidth
              label="Select a registered app"
              value={savedReportsAppId}
              onChange={(event) => setSavedReportsAppId(event.target.value)}
            >
              <MenuItem value="">
                <em>Select a registered app</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </TextField>
          </Paper>
          {savedReports.length === 0 ? (
            <Alert severity="info">No saved review analysis reports for the selected app.</Alert>
          ) : (
            <Grid container spacing={2}>
              {savedReports.map((report) => (
                <Grid item xs={12} md={6} key={report.reportId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">{report.appName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.savedAt?.toLocaleString() || report.metadata.generatedAt.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {report.analysis.summary}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Reviews: {report.reviewCount}
                        {typeof report.avgRating === 'number'
                          ? ` | Avg rating: ${report.avgRating.toFixed(2)}`
                          : ''}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => {
                          setCurrentReport(report);
                          setTab(1);
                        }}
                      >
                        Open
                      </Button>
                      <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDeleteReport(report)}>
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

function InsightSection({ title, insights }: { title: string; insights: ReviewThemeInsight[] }) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1.2 }}>
        {title}
      </Typography>
      {insights.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No insights generated.
        </Typography>
      ) : (
        insights.map((insight, index) => (
          <Box key={`${title}-${index}`} sx={{ mb: 1.6 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {insight.theme}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.4 }}>
              {insight.summary}
            </Typography>
            {insight.evidence.length > 0 ? (
              <Box sx={{ mt: 0.7, display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                {insight.evidence.map((item, itemIndex) => (
                  <Chip key={`${insight.theme}-${itemIndex}`} label={item} size="small" variant="outlined" />
                ))}
              </Box>
            ) : null}
          </Box>
        ))
      )}
    </Paper>
  );
}

function ActionSection({
  title,
  items,
  onExportItem,
}: {
  title: string;
  items: ReviewActionItem[];
  onExportItem: (item: ReviewActionItem) => void;
}) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1.2 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No action items generated.
        </Typography>
      ) : (
        items.map((item) => (
          <Box key={item.id} sx={{ mb: 1.6, p: 1.2, border: '1px solid #eceff3', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {item.title}
              </Typography>
              <Chip label={item.priority.toUpperCase()} size="small" />
            </Box>
            <Typography variant="body2" sx={{ mt: 0.6 }}>
              {item.rationale}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.8, fontWeight: 600 }}>
              Implementation Steps
            </Typography>
            {item.implementationSteps.map((step, index) => (
              <Typography key={`${item.id}-step-${index}`} variant="body2">
                {index + 1}. {step}
              </Typography>
            ))}
            {(item.ownerSuggestion || item.expectedImpact) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
                {[item.ownerSuggestion ? `Owner: ${item.ownerSuggestion}` : '', item.expectedImpact ? `Impact: ${item.expectedImpact}` : '']
                  .filter(Boolean)
                  .join(' | ')}
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => onExportItem(item)}>
                Export Work Item
              </Button>
            </Box>
          </Box>
        ))
      )}
    </Paper>
  );
}
